import React from "react";
import {
  SlidersHorizontal,
  ChevronDown,
  Share2,
  Download,
  FileSpreadsheet,
  FileCode2,
  Search,
  X,
  Map,
  FilePlus,
  FolderOpen,
  Save,
  Info,
  Crosshair,
  Calculator,
} from "lucide-react";
import { Collapsible, CollapsibleContent } from "../ui/collapsible";
import { Switch } from "../ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../ui/dropdown-menu";

// Region selector options come from the dataset (`regions`); this is only the fallback before it loads.
export const REGIONS = [{ id: "us", label: "United States", currency: "USD" }];

export default function Toolbar({
  groups,
  plans,
  visible,
  onTogglePlan,
  onToggleGroup,
  onSelectAll,
  onSelectNone,
  onSaveSelection,
  saving,
  onDownload,
  onExport,
  filtersOpen,
  onToggleFilters,
  activeFilterCount,
  search,
  onSearchChange,
  diffOnly,
  onToggleDiff,
  timeline,
  onToggleTimeline,
  showPrices,
  onTogglePrices,
  priceMode,
  onPriceMode,
  region,
  regions,
  onRegionChange,
  featureMap,
  onMapNew,
  onMapLoad,
  onMapSave,
  onMapClose,
  pricing,
  onOpenLookup,
  onOpenCost,
  costTotal,
}) {
  const groupsWithPlans = groups
    .map((g) => ({ ...g, plans: plans.filter((p) => p.group === g.id) }))
    .filter((g) => g.plans.length > 0);

  return (
    <section className="toolbar glass" aria-label="License selection and filters" data-testid="comparisons-panel">
      <div className="toolbar-row">
        {groupsWithPlans.map((group) => {
          const allOn = group.plans.every((p) => visible.includes(p.id));
          return (
            <div className="plan-group" key={group.id} data-testid={`plan-group-${group.id}`}>
              <button
                type="button"
                className="plan-group-label"
                onClick={() => onToggleGroup(group.id, !allOn)}
                title={allOn ? `Hide all ${group.label}` : `Show all ${group.label}`}
                data-testid={`group-toggle-${group.id}`}
              >
                {group.tableLabel}
              </button>
              <div className="pills" role="group" aria-label={group.label}>
                {group.plans.map((plan) => (
                  <button
                    key={plan.id}
                    type="button"
                    className={`pill ${plan.addon ? "addon" : ""}`}
                    aria-pressed={visible.includes(plan.id)}
                    onClick={() => onTogglePlan(plan.id)}
                    title={plan.name}
                    data-testid={`plan-toggle-${plan.id}`}
                  >
                    {plan.short}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <div className="quick-links">
          <button type="button" onClick={onSelectAll} data-testid="select-all-btn">
            All
          </button>
          <span aria-hidden="true">/</span>
          <button type="button" onClick={onSelectNone} data-testid="select-none-btn">
            None
          </button>
        </div>
        <div className="spacer" />
        {costTotal && (
          <button type="button" className="cost-pill" onClick={onOpenCost} title="Open cost calculator" data-testid="cost-total-pill">
            <Calculator size={13} aria-hidden="true" />
            <span className="mono">{costTotal}</span>
            <span className="per">/ mo</span>
          </button>
        )}
        <button type="button" className="tb-btn feature cost" onClick={onOpenCost} title="Seat-based cost calculator" data-testid="cost-btn">
          <span className="ico">
            <Calculator size={14} aria-hidden="true" />
          </span>
          Cost Calculator
        </button>
        <button
          type="button"
          className="tb-btn feature lookup"
          onClick={onOpenLookup}
          title="Find the cheapest license or add-on that includes a feature (Ctrl+K)"
          data-testid="lookup-btn"
        >
          <span className="ico">
            <Crosshair size={14} aria-hidden="true" />
          </span>
          Which License?
          <kbd aria-hidden="true">Ctrl K</kbd>
        </button>
        <span className="tools-divider" aria-hidden="true" />
        <button
          type="button"
          className={`tb-btn ${filtersOpen ? "active" : ""}`}
          onClick={onToggleFilters}
          aria-expanded={filtersOpen}
          data-testid="filters-toggle"
        >
          <SlidersHorizontal size={15} aria-hidden="true" />
          Filters
          {activeFilterCount > 0 && <span className="count">{activeFilterCount}</span>}
          <ChevronDown
            size={14}
            aria-hidden="true"
            style={{ transition: "transform 200ms ease", transform: filtersOpen ? "rotate(180deg)" : "none" }}
          />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="tb-btn" data-testid="export-menu">
              <Download size={15} aria-hidden="true" /> Export
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onExport} data-testid="export-btn">
              <FileSpreadsheet size={14} /> CSV spreadsheet
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onDownload} data-testid="download-btn">
              <FileCode2 size={14} /> Standalone HTML
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          className="tb-btn primary"
          onClick={onSaveSelection}
          disabled={saving}
          data-testid="save-selection-btn"
        >
          <Share2 size={15} aria-hidden="true" /> {saving ? "Saving..." : "Save & share"}
        </button>
      </div>

      <Collapsible open={filtersOpen} onOpenChange={onToggleFilters}>
        <CollapsibleContent className="filters" data-testid="filters-bar">
          <div className="filters-inner">
            <div className="search">
              <Search size={14} aria-hidden="true" />
              <input
                id="feature-search"
                type="search"
                placeholder="Search features..."
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                aria-label="Search for a feature"
                data-testid="feature-search"
              />
              {search && (
                <button type="button" className="clear" onClick={() => onSearchChange("")} aria-label="Clear search">
                  <X size={13} />
                </button>
              )}
            </div>

            <div className="filter-item">
              <Switch id="diff-switch" checked={diffOnly} onCheckedChange={onToggleDiff} data-testid="differences-btn" />
              <label htmlFor="diff-switch">Differences only</label>
            </div>
            <div className="filter-item">
              <Switch id="timeline-switch" checked={timeline} onCheckedChange={onToggleTimeline} data-testid="timeline-btn" />
              <label htmlFor="timeline-switch">Timeline</label>
            </div>
            <div className="filter-item">
              <Switch id="prices-switch" checked={showPrices} onCheckedChange={onTogglePrices} data-testid="show-prices-toggle" />
              <label htmlFor="prices-switch">Prices</label>
            </div>

            <div className="filter-sep" />

            <div className="seg" role="group" aria-label="Teams bundling">
              <button
                type="button"
                className={priceMode === "teams" ? "on" : ""}
                onClick={() => onPriceMode("teams")}
                data-testid="price-mode-teams"
              >
                With Teams
              </button>
              <button
                type="button"
                className={priceMode === "noTeams" ? "on" : ""}
                onClick={() => onPriceMode("noTeams")}
                data-testid="price-mode-noteams"
              >
                No Teams
              </button>
            </div>
            <Select value={region} onValueChange={onRegionChange}>
              <SelectTrigger className="region-trigger" aria-label="Pricing region" data-testid="region-select">
                <SelectValue placeholder="Region" />
              </SelectTrigger>
              <SelectContent>
                {(regions?.length ? regions : REGIONS).map((rg) => (
                  <SelectItem key={rg.id} value={rg.id} data-testid={`region-option-${rg.id}`}>
                    {rg.label} ({rg.currency})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="filter-item" style={{ color: "var(--text-3)", fontSize: 12 }} title={pricing?.note}>
              <Info size={13} aria-hidden="true" /> Effective {pricing?.effective}
            </span>

            <div className="filter-sep" />

            <div className="filter-item">
              <Map size={14} aria-hidden="true" />
              <span>Feature map</span>
              {featureMap && (
                <span className="map-name-chip" data-testid="active-map-name">
                  {featureMap.name}
                </span>
              )}
            </div>
            <button type="button" className="tb-btn sm" onClick={onMapNew} data-testid="map-new-btn">
              <FilePlus size={13} /> New
            </button>
            <button type="button" className="tb-btn sm" onClick={onMapLoad} data-testid="map-load-btn">
              <FolderOpen size={13} /> Load
            </button>
            <button type="button" className="tb-btn sm" onClick={onMapSave} disabled={!featureMap} data-testid="map-save-btn">
              <Save size={13} /> Save
            </button>
            <button type="button" className="tb-btn sm" onClick={onMapClose} disabled={!featureMap} data-testid="map-close-btn">
              <X size={13} /> Close
            </button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}
